import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import type { Booking } from "@/hooks/useBookings";

interface AppBookingItemProps {
  booking: Booking;
}

const formatTime = (t: string) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const AppBookingItem = ({ booking }: AppBookingItemProps) => {
  const navigate = useNavigate();
  const status = booking.status.toLowerCase();
  const isCompleted = status === "completed";
  const isCancelled = status === "cancelled";
  const isCheckedIn = !!booking.checked_in_at && !booking.checked_out_at;

  const badge = isCancelled ? (
    <Badge variant="destructive" className="text-[10px]">Cancelled</Badge>
  ) : isCompleted ? (
    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
      Completed
    </Badge>
  ) : isCheckedIn ? (
    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">
      In Session
    </Badge>
  ) : (
    <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">
      Confirmed
    </Badge>
  );

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(`/app/booking/${booking.id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-semibold leading-tight flex-1">
            {booking.service_name}
          </h3>
          {badge}
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {format(new Date(booking.booking_date), "MMM d")}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatTime(booking.start_time)} – {formatTime(booking.end_time)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm">
            £{booking.price.toFixed(2)}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
};

export default AppBookingItem;
